import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Skeleton } from '@/components/ui/skeleton'
import type { AdminLocation } from '@/lib/api/admin-locations'

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN ?? ''

interface SubscriberMapProps {
  locations: AdminLocation[]
  isLoading: boolean
}

function toGeoJSON(locations: AdminLocation[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: locations
      .filter((l) => {
        const lat = Number(l.latitude)
        const lng = Number(l.longitude)
        return !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)
      })
      .map((l) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [Number(l.longitude), Number(l.latitude)],
        },
        properties: {
          id: l.id,
          name: l.name,
          airport_code: l.airport_code ?? '',
          airport_name: l.airport_name ?? '',
          layout_count: l.layout_count,
        },
      })),
  }
}

export function SubscriberMap({ locations, isLoading }: SubscriberMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  useEffect(() => {
    if (!containerRef.current || !mapboxgl.accessToken) return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98.5, 39.8], // center of US
      zoom: 3,
      attributionControl: false,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'top-right')

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Update data when locations change
  useEffect(() => {
    const map = mapRef.current
    if (!map || !locations.length) return

    function addLayers() {
      const geojson = toGeoJSON(locations)

      // Remove existing source/layers if re-rendering
      if (map!.getSource('locations')) {
        map!.removeLayer('clusters')
        map!.removeLayer('cluster-count')
        map!.removeLayer('unclustered-point')
        map!.removeSource('locations')
      }

      map!.addSource('locations', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      })

      // Cluster circles
      map!.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'locations',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#8B5CF6', // indigo for small clusters
            10,
            '#7C3AED', // darker for medium
            30,
            '#6D28D9', // darkest for large
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18,  // small
            10, 24, // medium
            30, 32, // large
          ],
          'circle-opacity': 0.85,
        },
      })

      // Cluster count labels
      map!.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'locations',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: {
          'text-color': '#ffffff',
        },
      })

      // Individual location dots
      map!.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'locations',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#8B5CF6',
          'circle-radius': 7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      })

      // Click on cluster → zoom in
      map!.on('click', 'clusters', (e) => {
        const features = map!.queryRenderedFeatures(e.point, { layers: ['clusters'] })
        if (!features.length) return
        const clusterId = features[0].properties?.cluster_id
        const source = map!.getSource('locations') as mapboxgl.GeoJSONSource
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom == null) return
          const geom = features[0].geometry
          if (geom.type === 'Point') {
            map!.easeTo({ center: geom.coordinates as [number, number], zoom })
          }
        })
      })

      // Click on individual point → popup
      map!.on('click', 'unclustered-point', (e) => {
        const features = map!.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] })
        if (!features.length) return
        const props = features[0].properties
        const geom = features[0].geometry
        if (!props || geom.type !== 'Point') return

        const html = `
          <div style="font-family: system-ui; font-size: 13px;">
            <strong>${props.name}</strong>
            ${props.airport_code ? `<br/><span style="color: #6B7280;">${props.airport_code}${props.airport_name ? ` — ${props.airport_name}` : ''}</span>` : ''}
            <br/><span style="color: #6B7280;">${props.layout_count} layout${props.layout_count !== 1 ? 's' : ''}</span>
          </div>
        `

        new mapboxgl.Popup({ offset: 12, maxWidth: '250px' })
          .setLngLat(geom.coordinates as [number, number])
          .setHTML(html)
          .addTo(map!)
      })

      // Cursor changes
      map!.on('mouseenter', 'clusters', () => { map!.getCanvas().style.cursor = 'pointer' })
      map!.on('mouseleave', 'clusters', () => { map!.getCanvas().style.cursor = '' })
      map!.on('mouseenter', 'unclustered-point', () => { map!.getCanvas().style.cursor = 'pointer' })
      map!.on('mouseleave', 'unclustered-point', () => { map!.getCanvas().style.cursor = '' })

      // Fit bounds to all points
      if (geojson.features.length > 0) {
        const bounds = new mapboxgl.LngLatBounds()
        geojson.features.forEach((f) => {
          if (f.geometry.type === 'Point') {
            bounds.extend(f.geometry.coordinates as [number, number])
          }
        })
        map!.fitBounds(bounds, { padding: 50, maxZoom: 12 })
      }
    }

    if (map.isStyleLoaded()) {
      addLayers()
    } else {
      map.on('load', addLayers)
    }
  }, [locations])

  if (isLoading) {
    return <Skeleton className="h-full w-full rounded-md" />
  }

  if (!mapboxgl.accessToken) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Mapbox token not configured. Set VITE_MAPBOX_TOKEN in .env.local
      </div>
    )
  }

  return <div ref={containerRef} className="h-full w-full rounded-md" />
}
